import { useCallback, useMemo } from "react";
import { mnemonicToPrivateKey } from "@ton/crypto";
import { useAtom } from "jotai";
import { activeWalletIdAtom, storedWalletsAtom, walletsCounterAtom } from "../state/keys";
import { getWalletContract } from "../utils/getWalletContract";
import { decodePrivateKeyByPin, encodePrivateKeyByPin } from "../utils/pincode";
export const useTonWallets = () => {
    const [wallets, setWallets] = useAtom(storedWalletsAtom);
    const [walletsCounter, setWalletsCounter] = useAtom(walletsCounterAtom);
    const [activeWalletId, setAtiveWalletId] = useAtom(activeWalletIdAtom);
    const selectWallet = useCallback((id) => {
        setAtiveWalletId(id);
    }, []);
    const disconnectWallet = useCallback((id) => {
        const newWallets = wallets.filter((wallet) => wallet.id !== id);
        setWallets(newWallets);
        if (activeWalletId === id) {
            selectWallet(newWallets[0]?.id);
        }
    }, [activeWalletId, wallets]);
    const addWallet = useCallback(async (mnemonics, pin, walletVersion = "V5R1") => {
        const keyPair = await mnemonicToPrivateKey(mnemonics);
        const encodedMnemonics = encodePrivateKeyByPin(mnemonics, pin);
        const walletContract = getWalletContract(walletVersion).create({
            workchain: 0,
            publicKey: keyPair.publicKey,
        });
        const walletAddress = walletContract.address.toString();
        const alreadyExistsId = wallets.find((wallet) => wallet.address === walletAddress)?.id;
        if (alreadyExistsId) {
            selectWallet(alreadyExistsId);
            return;
        }
        const id = crypto.randomUUID();
        setWalletsCounter((prev) => prev + 1);
        const wallet = {
            id,
            address: walletContract.address.toString(),
            publicKey: keyPair.publicKey.toString("hex"),
            encodedMnemonics,
            walletVersion,
            walletName: `Wallet ${walletsCounter}`,
        };
        selectWallet(id);
        setWallets((prev) => [...prev, wallet]);
    }, [wallets.length, walletsCounter]);
    const updatePin = useCallback((oldPin, newPin) => {
        const newWallets = wallets.map((wallet) => {
            const mnemonic = decodePrivateKeyByPin(wallet.encodedMnemonics, oldPin);
            const newEncodedMnemonics = encodePrivateKeyByPin(mnemonic, newPin);
            return { ...wallet, encodedMnemonics: newEncodedMnemonics };
        });
        setWallets(newWallets);
    }, [wallets]);
    const _forceAddWallet = useCallback(async (wallet) => {
        const id = crypto.randomUUID();
        setWalletsCounter((prev) => prev + 1);
        const newWallet = {
            id,
            address: wallet.address,
            publicKey: wallet.publicKey,
            encodedMnemonics: wallet.privateKey,
            walletVersion: wallet.walletVersion,
            walletName: wallet.walletName,
        };
        selectWallet(id);
        setWallets((prev) => [...prev, newWallet]);
    }, []);
    return useMemo(() => ({
        disconnectWallet,
        selectWallet,
        addWallet,
        updatePin,
        list: wallets,
        _forceAddWallet,
    }), [disconnectWallet, selectWallet, addWallet, wallets]);
};
//# sourceMappingURL=useTonWallets.js.map